import React, { useState } from 'react';
import {
  AlertBanner,
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import { useHistory } from 'react-router-dom';

import { isEmpty, useInvertedLocalize } from '../../../../../utils';
import {
  type Provider,
  type ProviderResponse,
} from '../../../data-layer/types';
import { useUpdateIDPProvider } from '../../../hooks';
import ProviderIcon from '../ProviderIcon/ProviderIcon';

import invertedMessages from './messages';

import './Header.css';

interface HeaderProps {
  currentProvider: ProviderResponse | undefined;
  provider: Provider;
  resetBanners: () => void;
  toggleConfig?: Record<string, string | undefined>;
}

function Header({
  currentProvider,
  provider,
  resetBanners,
  toggleConfig,
}: HeaderProps) {
  const { mutate: updateIDPConfig } = useUpdateIDPProvider();
  const l = useInvertedLocalize(invertedMessages);
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState('');
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);

  const closeHandler = () => {
    history.push('/providers', {});
  };

  const successHandler = (action: string, title: string) => {
    history.push('/providers', { action, title });
  };

  return (
    <div>
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showCriticalBanner}
        onCancel={() => setShowCriticalBanner(false)}
        isFloating
        position={{
          type: 'absolute',
          top: '8rem',
          left: '7rem',
        }}
        shouldAutoDismiss
        isColonVisible={false}
      />
      {currentProvider && !isEmpty(currentProvider.config) && (
        <div className='Ext-IDDataWebSettings-header'>
          <Box className='Ext-IDDataWebSettings-header-text'>
            <ButtonIcon
              size='large'
              kind='ghost'
              icon={<Icon kind='ChevronLeft' />}
              onClick={() => {
                resetBanners();
                closeHandler();
              }}
              ariaLabel={l('Close')}
            />

            <ProviderIcon
              provider={provider}
              iconClassName='Ext-IDDataWebSettings-icon'
              logoClassName='Ext-IDPProviders-contentWrapper-content-card-saviyntIconWrapper-saviyntIcon'
            />
            <Box className='Ext-IDDataWebSettings-header-flexbox'>
              <Typography kind='h2' color='--color-foreground-neutral-bold'>
                {provider.name}
              </Typography>
              <Chip
                size='xSmall'
                label={
                  currentProvider.status === 1 ? l('Enabled') : l('Disabled')
                }
                color={currentProvider.status === 1 ? 'success' : 'neutral'}
              />
            </Box>
          </Box>
          <Menu
            className='Ext-IDDataWebSettings-header-menu'
            options={[
              {
                label:
                  currentProvider.status === 0 ? l('Disable') : l('Enable'),
                value: currentProvider.status === 0 ? 'Disable' : 'Enable',
              },
            ]}
            CustomMenu={
              <ul role='listbox' tabIndex={-1} className='MenuMulti-items'>
                <MenuItem
                  option={{
                    label:
                      currentProvider.status === 0 ? l('Enable') : l('Disable'),
                    value: currentProvider.status === 0 ? 'Enable' : 'Disable',
                  }}
                  onClick={() => {
                    if (currentProvider.status === 0) {
                      const providerConfig = toggleConfig
                        ? ({
                            vendorName: currentProvider.vendorName,
                            status: 1,
                            config: toggleConfig,
                          } as ProviderResponse)
                        : (() => {
                            const config = currentProvider.config
                              ? { ...currentProvider.config }
                              : currentProvider.config;

                            if (config && 'PASSWORD' in config) {
                              delete config.PASSWORD;
                            }

                            return {
                              ...currentProvider,
                              status: 1,
                              config,
                            } as ProviderResponse;
                          })();

                      updateIDPConfig(providerConfig, {
                        onSuccess(response) {
                          if (response.errorMessage) {
                            setErrorMessage(response.errorMessage);
                            setShowCriticalBanner(true);
                          } else {
                            successHandler('Enabled', provider.name);
                          }
                        },
                      });
                    } else {
                      const providerConfig = toggleConfig
                        ? ({
                            vendorName: currentProvider.vendorName,
                            status: 0,
                            config: toggleConfig,
                          } as ProviderResponse)
                        : (() => {
                            const config = currentProvider.config
                              ? { ...currentProvider.config }
                              : currentProvider.config;

                            if (config && 'PASSWORD' in config) {
                              delete config.PASSWORD;
                            }

                            return {
                              ...currentProvider,
                              status: 0,
                              config,
                            } as ProviderResponse;
                          })();

                      updateIDPConfig(providerConfig, {
                        onSuccess(response) {
                          if (response.errorMessage) {
                            setErrorMessage(response.errorMessage);
                            setShowCriticalBanner(true);
                          } else {
                            successHandler('Disabled', provider.name);
                          }
                        },
                      });
                    }
                  }}
                  tabIndex={0}
                />
              </ul>
            }
            trigger={<Icon kind='MoreVertical' size='medium' />}
          />
        </div>
      )}
      {isEmpty(currentProvider) && (
        <div className='Ext-IDDataWebSettings-breadcrumb'>
          <div className='Ext-IDDataWebSettings-breadcrumb-header'>
            <Typography
              kind='body2-bold'
              color='--color-foreground-primary-medium'>
              {l('Identity Verification')}
            </Typography>
            <Icon kind='ChevronRight' size='xSmall' />
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {provider.name}
            </Typography>
          </div>
          <div className='Ext-IDDataWebSettings-breadcrumb-provider'>
            <ProviderIcon
              provider={provider}
              iconClassName='Ext-IDDataWebSettings-icon'
              logoClassName='Ext-IDPProviders-contentWrapper-content-card-saviyntIconWrapper-saviyntIcon'
            />
            <Typography kind='h1'>{provider.name}</Typography>
          </div>
        </div>
      )}
    </div>
  );
}

Header.defaultProps = {
  toggleConfig: undefined,
};

export default Header;
